import React, { FC, ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import { ControlGroupSectionItemInUseContext } from './const';
import { isNullOrUndefined, isObject } from '../../../predicates/type';

type ControlGroupSectionProps = {
	className?: string;
	itemsMargin?: 'small' | 'medium' | 'large';
	dataTestId?: string;
	style?: React.CSSProperties;
};

/**
 * Provides margins between its child elements
 */
export const ControlGroupSection: FC<React.PropsWithChildren<ControlGroupSectionProps>> = ({
	children,
	className,
	itemsMargin = 'small',
	dataTestId,
	style,
}) => {
	const providerValue = useMemo(
		() => ({
			hasParentControlGroupSectionItem: true,
		}),
		[],
	);
	const componentClasses = classNames(
		classes.controlGroupSection,
		{
			[classes.controlGroupSection_marginSmall]: itemsMargin === 'small',
			[classes.controlGroupSection_marginMedium]: itemsMargin === 'medium',
			[classes.controlGroupSection_marginLarge]: itemsMargin === 'large',
		},
		className,
	);
	const itemClasses = classNames(classes.item, {
		[classes.item_marginSmall]: itemsMargin === 'small',
		[classes.item_marginMedium]: itemsMargin === 'medium',
		[classes.item_marginLarge]: itemsMargin === 'large',
	});

	return (
		<ControlGroupSectionItemInUseContext.Provider value={providerValue}>
			<div
				className={componentClasses}
				data-testid={dataTestId}
				style={style}
			>
				{React.Children.map(children, (child: ReactElement) => {
					const isHidden = isObject(child) && child?.props?.metadata?.hidden;

					if (isHidden || isNullOrUndefined(child)) {
						return null;
					}

					return <div className={itemClasses}>{child}</div>;
				})}
			</div>
		</ControlGroupSectionItemInUseContext.Provider>
	);
};
